import React from 'react';
import {
  Box, Paper, TextField, Button,
  Typography, Table, TableHead, TableRow, TableCell, TableBody,
  TablePagination, Divider, TableContainer,
  CircularProgress, Snackbar, Alert, Chip, Tooltip
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { Download } from '@mui/icons-material';

// --- Same method & API style as InventoryProdCompare.js
import Client from '../Client';
import Config from '../ClientConfig';
import BackofficeReportsApi from './backofficeReportsApi';

// ---------- helpers ----------
const pad2 = (n) => String(n).padStart(2, '0');
const today = () => new Date();
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const ymd = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const sameMonth = (d1, d2) => d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth();

const clampSameMonthAndOrder = (fromStr, toStr) => {
  if (!fromStr || !toStr) return { dateFrom: fromStr, dateTo: toStr, changed: false, note: '' };

  const from = new Date(fromStr);
  const to = new Date(toStr);

  const monthMin = ymd(new Date(from.getFullYear(), from.getMonth(), 1));
  const monthMax = ymd(new Date(from.getFullYear(), from.getMonth() + 1, 0));

  let newTo = toStr;
  let changed = false;

  if (to < new Date(monthMin)) {
    newTo = monthMin;
    changed = true;
  }

  if (to > new Date(monthMax)) {
    newTo = monthMax;
    changed = true;
  }

  if (new Date(newTo) < from) {
    newTo = fromStr;
    changed = true;
  }

  return { dateFrom: fromStr, dateTo: newTo, changed, note: '' };
};

// number formatting helpers
const fmtFixed = (n, d) => {
  const v = Number(n);
  if (!isFinite(v)) return '-';
  return v.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
};

const fmt0 = (n) => (n === null || n === undefined ? '-' : fmtFixed(Math.round(Number(n) || 0), 0));
const fmt2 = (n) => (n === null || n === undefined ? '-' : fmtFixed(Number(n), 2));

const fmtPct1 = (n) => {
  if (n === null || n === undefined || isNaN(Number(n))) return '-';
  const v = Number(n);
  return v.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + '%';
};

// API roots
const API_URL_PRODUCTS = Config.API_URL_PRODUCT + 'list_products/';
const API_URL_LOCATIONS = Config.API_URL_LOCATIONS + 'list_locations/';

const API_URL_PM_BY_LOCATION = BackofficeReportsApi.getMarginsByProductUrl();
const API_URL_PM_BY_PRODUCT_LOCATIONS = BackofficeReportsApi.getMarginsByProductLocationsUrl();

// ---------- CSV helpers ----------
const toCSV = (rows) => "\uFEFF" + rows.map(r => r.map(x => `"${String(x ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');

const downloadCSV = (filename, rows) => {
  const blob = new Blob([toCSV(rows)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');

  a.href = url;
  a.download = filename;
  a.click();

  URL.revokeObjectURL(url);
};

const detailCellSx = {
  py: { xs: 0.45, sm: 0.65, md: 0.75 },
  px: { xs: 0.45, sm: 0.7, md: 0.9 },
  whiteSpace: 'nowrap',
  fontSize: {
    xs: '0.72rem',
    sm: '0.82rem',
    md: '0.9rem',
  },
  fontVariantNumeric: 'tabular-nums',
};

const detailHeadSx = {
  ...detailCellSx,
  bgcolor: '#f8fafc',
  color: '#1a237e',
  fontWeight: 750,
  lineHeight: 1.1,
};

const paginationSx = {
  flexShrink: 0,
  borderTop: '1px solid rgba(15, 23, 42, 0.06)',
  minHeight: { xs: 38, sm: 50 },
  overflow: 'hidden',
  '& .MuiTablePagination-toolbar': {
    minHeight: { xs: 38, sm: 50 },
    px: { xs: 0.25, sm: 1 },
    flexWrap: 'nowrap',
    gap: { xs: 0.25, sm: 0.5 },
  },
  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
    fontSize: { xs: '0.72rem', sm: '0.86rem' },
    m: 0,
  },
  '& .MuiTablePagination-actions': {
    ml: { xs: 0.25, sm: 1 },
  },
  '& .MuiInputBase-root': {
    fontSize: { xs: '0.72rem', sm: '0.86rem' },
    mr: { xs: 0.25, sm: 1 },
  },
  '& .MuiIconButton-root': {
    p: { xs: 0.35, sm: 0.5 },
  },
};

const chipSx = {
  height: { xs: 22, sm: 26 },
  borderRadius: '9px',
  fontWeight: 650,
  fontSize: { xs: '0.66rem', sm: '0.78rem' },
  '& .MuiChip-label': {
    px: { xs: 0.55, sm: 0.8 },
  },
};

// ---------------------------------------------
// Base toolbar with month-limited date pickers + filters
// ---------------------------------------------
class MarginCommonToolbar extends React.Component {
  state = {
    client: new Client('none'),
    products: [],
    locations: [],
    dateFrom: ymd(startOfMonth(today())),
    dateTo: ymd(today()),
    productOpts: [],
    productSelOne: null,
    locationOpts: [],
    locationSel: [],
    locationSelOne: null,
    deliverySel: '',
    loadingLists: false,
    error: '',
    adjustNote: '',
  };

  componentDidMount() {
    this.bootstrap();
  }

  async bootstrap() {
    this.setState({ loadingLists: true });

    try {
      const [prodResp, locResp] = await Promise.all([
        this.state.client.getData(API_URL_PRODUCTS, { isForSale: 1, orderBy_name: true }) || {},
        this.state.client.getData(API_URL_LOCATIONS, { status: 1 }) || {},
      ]);

      const prods = Array.isArray(prodResp?.products)
        ? prodResp.products
        : Array.isArray(prodResp)
          ? prodResp
          : prodResp?.results || [];

      const locs = Array.isArray(locResp?.locations)
        ? locResp.locations
        : Array.isArray(locResp)
          ? locResp
          : locResp?.results || [];

      const productOpts = (prods || []).map(p => ({
        value: String(p.iref || p.id || p.uid || p.code),
        label: String(p.name || p.productName || p.description || p.code || 'Unnamed'),
      }));

      const locationOpts = (locs || []).map(l => ({
        value: String(l.iref || l.id || l.uid),
        label: String(l.name || l.description || 'Unknown'),
      }));

      locationOpts.sort((a, b) => a.label.localeCompare(b.label));

      this.setState({ productOpts, locationOpts, loadingLists: false }, () => this.propagate());
    } catch (e) {
      this.setState({ loadingLists: false, error: String(e?.message || e) });
    }
  }

  setPreset = (key) => {
    const base = this.state.dateFrom ? new Date(this.state.dateFrom) : today();

    if (key === 'FULL') {
      this.setState({
        dateFrom: ymd(startOfMonth(base)),
        dateTo: ymd(endOfMonth(base)),
      }, this.propagate);
    } else if (key === 'MTD') {
      this.setState({
        dateFrom: ymd(startOfMonth(base)),
        dateTo: ymd(base),
      }, this.propagate);
    } else if (key === 'FIRST10') {
      this.setState({
        dateFrom: ymd(startOfMonth(base)),
        dateTo: ymd(new Date(base.getFullYear(), base.getMonth(), 10)),
      }, this.propagate);
    }
  };

  propagate = () => {
    const { dateFrom, dateTo } = this.state;

    const d1 = new Date(dateFrom);
    const d2 = new Date(dateTo);

    let error = '';

    if (!sameMonth(d1, d2)) error = 'Period must be within the same month.';
    if (d1 > d2) error = 'dateFrom cannot be after dateTo.';

    this.setState({ error });
  };

  applyFilters = () => {
    const { dateFrom, dateTo, productSelOne, locationSelOne, deliverySel, error } = this.state;

    if (error) return;

    this.props.onChange?.({
      dateFrom,
      dateTo,
      productIrefs: productSelOne ? [productSelOne.value] : [],
      locationIrefs: locationSelOne ? [locationSelOne.value] : [],
      delivery: deliverySel,
    });
  };

  render() {
    const { dateFrom, dateTo, productOpts, locationOpts, error, loadingLists } = this.state;

    const fieldSx = {
      minWidth: 0,
      '& .MuiInputBase-root': {
        height: 34,
        borderRadius: '12px',
      },
      '& .MuiInputBase-input': {
        fontSize: {
          xs: '16px',
          sm: '0.84rem',
        },
        px: {
          xs: 0.75,
          sm: 1,
        },
      },
    };

    const autoSx = {
      minWidth: 0,
      '& .MuiInputBase-root': {
        minHeight: 34,
        borderRadius: '12px',
        py: 0,
      },
      '& .MuiInputBase-input': {
        fontSize: {
          xs: '16px',
          sm: '0.84rem',
        },
      },
      '& .MuiInputLabel-root': {
        fontSize: {
          xs: '0.78rem',
          sm: '0.84rem',
        },
      },
    };

    return (
      <Paper
        elevation={0}
        sx={{
          p: { xs: 0.5, sm: 0.75 },
          mb: 0.75,
          borderRadius: '14px',
          border: '1px solid rgba(15, 23, 42, 0.06)',
          boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr 1fr',
              sm: 'repeat(4, minmax(0, 1fr))',
              md: 'repeat(6, minmax(0, 1fr))',
            },
            gap: {
              xs: 0.65,
              sm: 0.8,
            },
            alignItems: 'center',
          }}
        >
          <TextField
            label="From"
            type="date"
            size="small"
            value={dateFrom}
            onChange={(e) => {
              const nextFrom = e.target.value;
              const adj = clampSameMonthAndOrder(nextFrom, this.state.dateTo);

              this.setState(
                {
                  dateFrom: adj.dateFrom,
                  dateTo: adj.dateTo,
                  adjustNote: adj.changed ? adj.note : '',
                },
                this.propagate
              );
            }}
            InputLabelProps={{ shrink: true }}
            sx={fieldSx}
          />

          <TextField
            label="To"
            type="date"
            size="small"
            value={dateTo}
            onChange={(e) => {
              const nextTo = e.target.value;
              const adj = clampSameMonthAndOrder(this.state.dateFrom, nextTo);

              this.setState(
                {
                  dateFrom: adj.dateFrom,
                  dateTo: adj.dateTo,
                  adjustNote: adj.changed ? adj.note : '',
                },
                this.propagate
              );
            }}
            InputLabelProps={{ shrink: true }}
            sx={fieldSx}
          />

          <Autocomplete
            size="small"
            options={productOpts}
            value={this.state.productSelOne}
            onChange={(_e, v) =>
              this.setState(
                {
                  productSelOne: v || null,
                  locationSelOne: v ? null : this.state.locationSelOne,
                },
                this.propagate
              )
            }
            getOptionLabel={(o) => o?.label || ''}
            renderInput={(params) => (
              <TextField {...params} label="Product" placeholder="All" />
            )}
            sx={autoSx}
            disableClearable={false}
            disabled={!!this.state.locationSelOne}
          />

          <Autocomplete
            size="small"
            options={locationOpts}
            value={this.state.locationSelOne}
            onChange={(_e, v) =>
              this.setState(
                {
                  locationSelOne: v || null,
                  productSelOne: v ? null : this.state.productSelOne,
                },
                this.propagate
              )
            }
            getOptionLabel={(o) => o?.label || ''}
            renderInput={(params) => (
              <TextField {...params} label="Location" placeholder="All" />
            )}
            sx={autoSx}
            disableClearable={false}
            disabled={!!this.state.productSelOne}
          />

          <Autocomplete
            size="small"
            options={[
              { id: '', label: 'Any' },
              { id: '0', label: 'No' },
              { id: '1', label: 'Yes' },
            ]}
            value={[
              { id: '', label: 'Any' },
              { id: '0', label: 'No' },
              { id: '1', label: 'Yes' },
            ].find(x => x.id === this.state.deliverySel)}
            onChange={(_e, v) => this.setState({ deliverySel: (v?.id ?? '') })}
            getOptionLabel={(o) => o?.label || ''}
            renderInput={(params) => (
              <TextField {...params} label="Delivery" />
            )}
            sx={autoSx}
            isOptionEqualToValue={(opt, val) => (opt?.id ?? '') === (val?.id ?? '')}
          />

          <Button
            size="small"
            variant="contained"
            onClick={this.applyFilters}
            sx={{
              height: 34,
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 750,
              gridColumn: {
                xs: '1 / -1',
                sm: 'auto',
              },
              justifySelf: {
                xs: 'stretch',
                sm: 'end',
              },
            }}
          >
            Apply filters
          </Button>

          {loadingLists && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ gridColumn: '1 / -1', px: 0.25 }}
            >
              Loading lists…
            </Typography>
          )}

          {error && (
            <Typography
              variant="caption"
              color="error"
              sx={{ gridColumn: '1 / -1', px: 0.25 }}
            >
              {error}
            </Typography>
          )}

          {this.state.adjustNote && (
            <Typography
              variant="caption"
              color="warning.main"
              sx={{ gridColumn: '1 / -1', px: 0.25 }}
            >
              {this.state.adjustNote}
            </Typography>
          )}
        </Box>
      </Paper>
    );
  }
}

// ---------------------------------------------
// Page 1: Product Margin by Location
// ---------------------------------------------
export class ProductMarginByLocationPage extends React.Component {
  state = {
    client: new Client('none'),
    loading: false,
    rows: [],
    totals: null,
    page: 0,
    rowsPerPage: -1,
    lastFilters: null,
    toastOpen: false,
    toastMsg: '',
    cogaZeroProds: [],
    cogaZeroProdsMore: 0,
    cogaZeroExtra: 0,
  };

  notify = (msg) => this.setState({ toastOpen: true, toastMsg: msg });

  onFilters = async ({ dateFrom, dateTo, productIrefs, locationIrefs, delivery }) => {
    if (!dateFrom || !dateTo) return;

    const d1 = new Date(dateFrom);
    const d2 = new Date(dateTo);

    if (!sameMonth(d1, d2) || d1 > d2) return;

    const params = { dateFrom, dateTo };

    if (delivery === '0' || delivery === '1') params.delivery = delivery;

    let url = API_URL_PM_BY_LOCATION;

    if ((productIrefs?.length === 1) && !(locationIrefs?.length)) {
      url = API_URL_PM_BY_PRODUCT_LOCATIONS;
      params.product = productIrefs[0];
    } else if ((locationIrefs?.length === 1) && !(productIrefs?.length)) {
      url = API_URL_PM_BY_LOCATION;
      params.location = locationIrefs[0];
    }

    this.setState({
      loading: true,
      rows: [],
      totals: null,
      page: 0,
      lastFilters: { dateFrom, dateTo, productIrefs, locationIrefs },
    });

    try {
      const data = await this.state.client.getData(url, params) || {};
      let rows = Array.isArray(data?.rows) ? data.rows : [];

      if (locationIrefs?.length > 1) {
        rows = rows.filter(r => !r.locationIref || locationIrefs.includes(String(r.locationIref)));
      }

      const allProds = Array.isArray(rows)
        ? rows
          .filter(r => Number(r.marginPct) === 100 || Number(r.unitCoga) === 0 || Number(r.cogs) === 0)
          .map(r => (r.productName || r.name || 'Product'))
        : [];

      const cogaZeroProds = allProds.slice(0, 5);
      const cogaZeroExtra = allProds.length > 5 ? allProds.length - 5 : 0;

      this.setState({
        rows,
        totals: data?.totals || null,
        loading: false,
        cogaZeroProds,
        cogaZeroExtra,
      });
    } catch (e) {
      this.notify(String(e?.message || e));
      this.setState({ loading: false });
    }
  };

  exportCSV = () => {
    const { rows, totals, lastFilters } = this.state;

    const productInfo = lastFilters?.productIrefs?.length === 1
      ? '1 selected'
      : 'All Products';

    const locationInfo = lastFilters?.locationIrefs?.length === 1
      ? '1 selected'
      : 'All Locations';

    const meta = [
      ['Report', 'Product Margin by Location'],
      ['Period', `${lastFilters?.dateFrom || ''} to ${lastFilters?.dateTo || ''}`],
      ['Products', productInfo],
      ['Locations', locationInfo],
      ['Generated At', new Date().toISOString()],
    ];

    const header = ['Location', 'Product', 'Qty', 'Gross', 'VAT', 'Net', 'UnitCOGA', 'COGS', 'Margin', 'Margin%'];
    const out = [...meta, [], header];

    rows.forEach(r => {
      out.push([
        r.locationName ?? 'All Locations',
        r.productName ?? 'All Products',
        fmt0(r.qty),
        fmt0(r.gross),
        fmt0(r.vat),
        fmt0(r.net),
        fmt2(r.unitCogaCost),
        fmt0(r.cogs),
        fmt0(r.margin),
        r.marginPct == null ? '' : fmtPct1(r.marginPct),
      ]);
    });

    if (totals) {
      out.push([]);
      out.push([
        'TOTALS', '',
        fmt0(totals.qty),
        fmt0(totals.gross),
        fmt0(totals.vat),
        fmt0(totals.net),
        '',
        fmt0(totals.cogs),
        fmt0(totals.margin),
        totals.marginPct == null ? '' : fmtPct1(totals.marginPct),
      ]);
    }

    const dateLabel = lastFilters ? `${lastFilters.dateFrom}_to_${lastFilters.dateTo}` : 'period';
    downloadCSV(`product_margin_by_location_${dateLabel}.csv`, out);
  };

  renderTable() {
    const { rows, totals, page, rowsPerPage } = this.state;
    const slice = rowsPerPage > 0 ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : rows;

    const chips = totals ? [
      { label: 'Qty', val: fmt0(totals.qty) },
      { label: 'Value', val: fmt0(totals.gross) },
      { label: 'VAT', val: fmt0(totals.vat) },
      { label: 'Net', val: fmt0(totals.net) },
      { label: 'COGS', val: fmt0(totals.cogs) },
      { label: 'Margin', val: fmt0(totals.margin) },
      { label: '%', val: totals.marginPct == null ? '-' : fmtPct1(totals.marginPct) },
    ] : [];

    return (
      <Paper
        variant="outlined"
        sx={{
          p: 0,
          height: {
            xs: 'calc(100dvh - 270px)',
            sm: 'calc(100dvh - 260px)',
            md: 'calc(100vh - 280px)',
          },
          minHeight: {
            xs: 430,
            sm: 460,
            md: 480,
          },
          maxHeight: {
            xs: 'calc(100dvh - 180px)',
            md: 680,
          },
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: '14px',
          borderColor: 'rgba(15, 23, 42, 0.08)',
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 0.75,
            p: {
              xs: 0.5,
              sm: 0.85,
            },
            borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 0.4,
              flexWrap: 'wrap',
              minWidth: 0,
              flex: 1,
            }}
          >
            {chips.map((c, i) => (
              <Chip
                key={i}
                size="small"
                variant="outlined"
                label={<span><strong>{c.label}:</strong> {c.val}</span>}
                sx={chipSx}
              />
            ))}
          </Box>

          <Tooltip title="Download CSV">
            <span>
              <Button
                size="small"
                variant="outlined"
                onClick={this.exportCSV}
                disabled={!rows.length}
                sx={{
                  minWidth: { xs: 34, sm: 96 },
                  width: { xs: 34, sm: 'auto' },
                  height: { xs: 30, sm: 32 },
                  borderRadius: '11px',
                  textTransform: 'none',
                  fontWeight: 750,
                  flexShrink: 0,
                  px: { xs: 0, sm: 1 },
                }}
              >
                <Download fontSize="small" />
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' }, ml: 0.5 }}>
                  CSV
                </Box>
              </Button>
            </span>
          </Tooltip>
        </Box>

        <TableContainer
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            width: '100%',
          }}
        >
          <Table
            size="small"
            stickyHeader
            sx={{
              minWidth: {
                xs: 980,
                sm: 1040,
                md: '100%',
              },
              '& .MuiTableCell-stickyHeader': {
                backgroundColor: '#f8fafc',
                zIndex: 3,
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...detailHeadSx, minWidth: 150 }}>Location</TableCell>
                <TableCell sx={{ ...detailHeadSx, minWidth: 220 }}>Product</TableCell>
                <TableCell align="right" sx={detailHeadSx}>Qty</TableCell>
                <TableCell align="right" sx={detailHeadSx}>Value</TableCell>
                <TableCell align="right" sx={detailHeadSx}>VAT</TableCell>
                <TableCell align="right" sx={detailHeadSx}>Net</TableCell>
                <TableCell align="right" sx={detailHeadSx}>Unit COGA</TableCell>
                <TableCell align="right" sx={detailHeadSx}>COGS</TableCell>
                <TableCell align="right" sx={detailHeadSx}>Margin</TableCell>
                <TableCell align="right" sx={detailHeadSx}>%</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {slice.map((r, idx) => (
                <TableRow key={idx} hover>
                  <TableCell sx={detailCellSx}>{r.locationName ?? 'All Locations'}</TableCell>
                  <TableCell sx={detailCellSx}>{r.productName}</TableCell>
                  <TableCell align="right" sx={detailCellSx}>{fmt0(r.qty)}</TableCell>
                  <TableCell align="right" sx={detailCellSx}>{fmt0(r.gross)}</TableCell>
                  <TableCell align="right" sx={detailCellSx}>{fmt0(r.vat)}</TableCell>
                  <TableCell align="right" sx={detailCellSx}>{fmt0(r.net)}</TableCell>
                  <TableCell align="right" sx={detailCellSx}>{fmt2(r.unitCogaCost)}</TableCell>
                  <TableCell align="right" sx={detailCellSx}>{fmt0(r.cogs)}</TableCell>
                  <TableCell align="right" sx={detailCellSx}>{fmt0(r.margin)}</TableCell>
                  <TableCell align="right" sx={detailCellSx}>{r.marginPct == null ? '-' : fmtPct1(r.marginPct)}</TableCell>
                </TableRow>
              ))}

              {!slice.length && (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={detailCellSx}>
                    No results
                  </TableCell>
                </TableRow>
              )}
            </TableBody>

            {totals && (
              <tfoot>
                <TableRow>
                  <TableCell sx={{ ...detailCellSx, fontWeight: 700 }}>Totals</TableCell>
                  <TableCell sx={detailCellSx} />
                  <TableCell align="right" sx={{ ...detailCellSx, fontWeight: 700 }}>{fmt0(totals.qty)}</TableCell>
                  <TableCell align="right" sx={{ ...detailCellSx, fontWeight: 700 }}>{fmt0(totals.gross)}</TableCell>
                  <TableCell align="right" sx={{ ...detailCellSx, fontWeight: 700 }}>{fmt0(totals.vat)}</TableCell>
                  <TableCell align="right" sx={{ ...detailCellSx, fontWeight: 700 }}>{fmt0(totals.net)}</TableCell>
                  <TableCell sx={detailCellSx} />
                  <TableCell align="right" sx={{ ...detailCellSx, fontWeight: 700 }}>{fmt0(totals.cogs)}</TableCell>
                  <TableCell align="right" sx={{ ...detailCellSx, fontWeight: 700 }}>{fmt0(totals.margin)}</TableCell>
                  <TableCell align="right" sx={{ ...detailCellSx, fontWeight: 700 }}>{totals.marginPct == null ? '-' : fmtPct1(totals.marginPct)}</TableCell>
                </TableRow>
              </tfoot>
            )}
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={rows.length}
          page={page}
          onPageChange={(_e, p) => this.setState({ page: p })}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => this.setState({ rowsPerPage: Number(e.target.value), page: 0 })}
          rowsPerPageOptions={[10, 25, { value: -1, label: 'All' }]}
          sx={paginationSx}
        />
      </Paper>
    );
  }

  render() {
    const { loading, toastOpen, toastMsg } = this.state;

    return (
      <Box
        sx={{
          width: '100%',
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        <MarginCommonToolbar onChange={this.onFilters} singleLocation />

        {Array.isArray(this.state.cogaZeroProds) && this.state.cogaZeroProds.length > 0 && (
          <Box sx={{ px: 0.75, textAlign: 'left', mb: 0.75, overflow: 'hidden' }}>
            <Typography
              variant="caption"
              color="warning.main"
              sx={{
                display: 'block',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontSize: { xs: '0.72rem', sm: '0.8rem' },
              }}
            >
              Products with cost 0: {this.state.cogaZeroProds.join(', ')}
              {this.state.cogaZeroProdsMore ? `, …${this.state.cogaZeroProdsMore} products more` : ''}
              {this.state.cogaZeroExtra > 0 ? ` ...${this.state.cogaZeroExtra} products more` : ''}
            </Typography>
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          this.renderTable()
        )}

        <Snackbar
          open={toastOpen}
          autoHideDuration={2400}
          onClose={() => this.setState({ toastOpen: false })}
        >
          <Alert
            severity="warning"
            variant="filled"
            onClose={() => this.setState({ toastOpen: false })}
          >
            {toastMsg}
          </Alert>
        </Snackbar>
      </Box>
    );
  }
}

// ---------------------------------------------
// Page 2: Margin by Location
// ---------------------------------------------
export class MarginByLocationsPage extends React.Component {
  state = {
    client: new Client('none'),
    loading: false,
    rows: [],
    totals: null,
    page: 0,
    rowsPerPage: -1,
    lastFilters: null,
    cogaZeroProds: [],
    cogaZeroProdsMore: 0,
    cogaZeroExtra: 0,
  };

  onFilters = async ({ dateFrom, dateTo, productIrefs, locationIrefs, delivery }) => {
    if (!dateFrom || !dateTo) return;

    const d1 = new Date(dateFrom);
    const d2 = new Date(dateTo);

    if (!sameMonth(d1, d2) || d1 > d2) return;

    const params = { dateFrom, dateTo };

    if (delivery === '0' || delivery === '1') params.delivery = delivery;
    if (productIrefs?.length) params.product = productIrefs.join(',');

    this.setState({
      loading: true,
      rows: [],
      totals: null,
      lastFilters: { dateFrom, dateTo, productIrefs, locationIrefs },
      page: 0,
    });

    try {
      const data = await this.state.client.getData(API_URL_PM_BY_PRODUCT_LOCATIONS, params) || {};
      let rows = Array.isArray(data?.rows) ? data.rows : [];

      if (locationIrefs?.length) {
        rows = rows.filter(r => locationIrefs.includes(String(r.locationIref)));
      }

      const allProds = Array.isArray(rows)
        ? rows
          .filter(r => Number(r.marginPct) === 100 || Number(r.unitCoga) === 0 || Number(r.cogs) === 0)
          .map(r => (r.productName || r.name || 'Product'))
        : [];

      const cogaZeroProds = allProds.slice(0, 5);
      const cogaZeroExtra = allProds.length > 5 ? allProds.length - 5 : 0;

      this.setState({
        rows,
        totals: data?.totals || null,
        loading: false,
        cogaZeroProds,
        cogaZeroExtra,
      });
    } catch (e) {
      this.setState({ loading: false });
    }
  };

  exportCSV = () => {
    const { rows, totals, lastFilters } = this.state;

    const header = ['Location', 'Qty', 'Value', 'VAT', 'Net', 'COGS', 'Margin', 'Margin%'];
    const out = [header];

    rows.forEach(r => out.push([
      r.locationName,
      fmt0(r.qty),
      fmt0(r.gross),
      fmt0(r.vat),
      fmt0(r.net),
      fmt0(r.cogs),
      fmt0(r.margin),
      r.marginPct == null ? '' : fmtPct1(r.marginPct),
    ]));

    if (totals) {
      out.push([]);
      out.push([
        'TOTALS',
        fmt0(totals.qty),
        fmt0(totals.gross),
        fmt0(totals.vat),
        fmt0(totals.net),
        fmt0(totals.cogs),
        fmt0(totals.margin),
        totals.marginPct == null ? '' : fmtPct1(totals.marginPct),
      ]);
    }

    const dateLabel = lastFilters ? `${lastFilters.dateFrom}_to_${lastFilters.dateTo}` : 'period';
    downloadCSV(`margin_by_locations_${dateLabel}.csv`, out);
  };

  renderTable() {
    const { rows, totals, page, rowsPerPage } = this.state;
    const slice = rowsPerPage > 0 ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : rows;

    const chips = totals ? [
      { label: 'Qty', val: fmt0(totals.qty) },
      { label: 'Value', val: fmt0(totals.gross) },
      { label: 'VAT', val: fmt0(totals.vat) },
      { label: 'Net', val: fmt0(totals.net) },
      { label: 'COGS', val: fmt0(totals.cogs) },
      { label: 'Margin', val: fmt0(totals.margin) },
      { label: '%', val: totals.marginPct == null ? '-' : fmtPct1(totals.marginPct) },
    ] : [];

    return (
      <Paper
        variant="outlined"
        sx={{
          p: 0,
          height: {
            xs: 'calc(100dvh - 300px)',
            sm: 'calc(100dvh - 280px)',
            md: 'calc(100vh - 320px)',
          },
          minHeight: {
            xs: 400,
            sm: 440,
            md: 460,
          },
          maxHeight: {
            xs: 'calc(100dvh - 200px)',
            md: 640,
          },
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: '14px',
          borderColor: 'rgba(15, 23, 42, 0.08)',
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 0.75,
            p: {
              xs: 0.5,
              sm: 0.85,
            },
            borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 0.4,
              flexWrap: 'wrap',
              minWidth: 0,
              flex: 1,
            }}
          >
            {chips.map((c, i) => (
              <Chip
                key={i}
                size="small"
                variant="outlined"
                label={<span><strong>{c.label}:</strong> {c.val}</span>}
                sx={chipSx}
              />
            ))}
          </Box>

          <Tooltip title="Download CSV">
            <span>
              <Button
                size="small"
                variant="outlined"
                onClick={this.exportCSV}
                disabled={!rows.length}
                sx={{
                  minWidth: { xs: 34, sm: 96 },
                  width: { xs: 34, sm: 'auto' },
                  height: { xs: 30, sm: 32 },
                  borderRadius: '11px',
                  textTransform: 'none',
                  fontWeight: 750,
                  flexShrink: 0,
                  px: { xs: 0, sm: 1 },
                }}
              >
                <Download fontSize="small" />
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' }, ml: 0.5 }}>
                  CSV
                </Box>
              </Button>
            </span>
          </Tooltip>
        </Box>

        <TableContainer
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            width: '100%',
          }}
        >
          <Table
            size="small"
            stickyHeader
            sx={{
              minWidth: {
                xs: 760,
                sm: 840,
                md: '100%',
              },
              '& .MuiTableCell-stickyHeader': {
                backgroundColor: '#f8fafc',
                zIndex: 3,
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...detailHeadSx, minWidth: 180 }}>Location</TableCell>
                <TableCell align="right" sx={detailHeadSx}>Qty</TableCell>
                <TableCell align="right" sx={detailHeadSx}>Value</TableCell>
                <TableCell align="right" sx={detailHeadSx}>VAT</TableCell>
                <TableCell align="right" sx={detailHeadSx}>Net</TableCell>
                <TableCell align="right" sx={detailHeadSx}>COGS</TableCell>
                <TableCell align="right" sx={detailHeadSx}>Margin</TableCell>
                <TableCell align="right" sx={detailHeadSx}>%</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {slice.map((r, idx) => (
                <TableRow key={idx} hover>
                  <TableCell sx={detailCellSx}>{r.locationName}</TableCell>
                  <TableCell align="right" sx={detailCellSx}>{fmt0(r.qty)}</TableCell>
                  <TableCell align="right" sx={detailCellSx}>{fmt0(r.gross)}</TableCell>
                  <TableCell align="right" sx={detailCellSx}>{fmt0(r.vat)}</TableCell>
                  <TableCell align="right" sx={detailCellSx}>{fmt0(r.net)}</TableCell>
                  <TableCell align="right" sx={detailCellSx}>{fmt0(r.cogs)}</TableCell>
                  <TableCell align="right" sx={detailCellSx}>{fmt0(r.margin)}</TableCell>
                  <TableCell align="right" sx={detailCellSx}>{r.marginPct == null ? '-' : fmtPct1(r.marginPct)}</TableCell>
                </TableRow>
              ))}

              {!slice.length && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={detailCellSx}>
                    No results
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={rows.length}
          page={page}
          onPageChange={(_e, p) => this.setState({ page: p })}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => this.setState({ rowsPerPage: Number(e.target.value), page: 0 })}
          rowsPerPageOptions={[10, 25, { value: -1, label: 'All' }]}
          sx={paginationSx}
        />
      </Paper>
    );
  }

  render() {
    const { loading } = this.state;

    return (
      <Box
        sx={{
          width: '100%',
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            mb: 0.75,
            fontWeight: 750,
            fontSize: { xs: '1rem', sm: '1.15rem' },
          }}
        >
          Margin by Location
        </Typography>

        <MarginCommonToolbar onChange={this.onFilters} singleLocation />

        {Array.isArray(this.state.cogaZeroProds) && this.state.cogaZeroProds.length > 0 && (
          <Box sx={{ px: 0.75, textAlign: 'left', mb: 0.75, overflow: 'hidden' }}>
            <Typography
              variant="caption"
              color="warning.main"
              sx={{
                display: 'block',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontSize: { xs: '0.72rem', sm: '0.8rem' },
              }}
            >
              Products with cost 0: {this.state.cogaZeroProds.join(', ')}
              {this.state.cogaZeroProdsMore ? `, …${this.state.cogaZeroProdsMore} products more` : ''}
              {this.state.cogaZeroExtra > 0 ? ` ...${this.state.cogaZeroExtra} products more` : ''}
            </Typography>
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          this.renderTable()
        )}
      </Box>
    );
  }
}

// Convenience wrapper to demo both
export default function ProductMarginPages() {
  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        overflow: 'hidden',
        bgcolor: 'background.default',
        py: 0,
        px: {
          xs: 0.5,
          sm: 1,
          md: 1.5,
        },
        boxSizing: 'border-box',
      }}
    >
      <ProductMarginByLocationPage />

      <Divider sx={{ my: { xs: 1.25, sm: 2 } }} />

      <MarginByLocationsPage />
    </Box>
  );
}
